import { useEffect, useCallback, useState } from "react";
import type { ModalMap } from "../modal/modalTypes";
import { useUser } from "../Context/UserContext";
import "./NotificationModal.css";
import useAPI from "../Hooks/useAPI";
import Button2 from "../Components/Button2/Button2";
import SecHeader from "../Components/SecHeader/SecHeader";
import { Bell } from "lucide-react";

type Props = ModalMap["notification"] & {
  closeModal: () => void;
};

interface UserMessage {
  messageid: number;
  messagetext: string;
  sentby: string;
  dateadded: string;
  breadonce: boolean;
  bautodisplay: boolean;
}

export function NotificationModal({ userid, closeModal }: Props) {
  const [Messages, setMessages] = useState<UserMessage[]>([]);
  const { user, setUser } = useUser();

  const [error, setError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [noData, setNoData] = useState(false);
  const [messageUpdated, setMessageUpdated] = useState(false);
  const {
    getUserMessages,
    UpdateUserMessage,
    postUserData,
    getMyPermissions,
    getMyRoles,
  } = useAPI();

  const fetchMessages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const response = await getUserMessages(userid);

      if (response?.data) {
        // setEngines(response.data);
        setNoData(false);

        setMessages(response.data);

        if (response?.data.length === 0) {
          setNoData(true);
        }
      } else {
        throw new Error("Failed to fetch user messages");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const MarkMessageAsRead = useCallback(
    async (messageid: number, userid: string) => {
      try {
        const response = await UpdateUserMessage(messageid, userid);
        setMessageUpdated(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "An error occurred");
      } finally {
      }
    },
    [],
  );

  const ProcessClose = useCallback(async () => {
    try {
      const freshUser = await postUserData(user!.email);
      if (user) {
        // Then get user permissions and roles
        const [permissions, roles] = await Promise.all([
          getMyPermissions(),
          getMyRoles(),
        ]);

        // Create user object with all data
        const userWithData = {
          ...freshUser.user,
          permissions: permissions || [],
          roles: roles || [],
        };

        setUser({
          ...user,
          ...userWithData,
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      closeModal();
    }
  }, []);

  useEffect(() => {
    fetchMessages();
  }, []);

  return (
    <div className="notificationModalOverlay">
      <div className="notificationModalContent">
        <div className="msg-header ">
          User Notifications
          <span className="notifybellWrapper">
            <Bell size={24} />
            {true && <span className="bellDot" />}
          </span>
        </div>
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <div className="loading-text">Loading...</div>
          </div>
        ) : (
          <p>
            {Messages.length > 0 ? (
              Messages.map((msg) => (
                <div key={msg.messageid} className="messagecontent">
                  <div className="message-card">
                    <div className="message-header">
                      <div className="message-header-row">
                        <span className="label">Message From:</span>
                        <span>{msg.sentby}</span>
                      </div>
                      <div className="message-header-row">
                        <span className="label">Date Posted:</span>
                        <span>{msg.dateadded}</span>
                      </div>
                    </div>
                    <div className="message-text">{msg.messagetext}</div>
                    <div className="message-buttons">
                      <Button2
                        name="markread"
                        text="Mark As Read"
                        type="button"
                        classname="submit-btn modal-submit"
                        onClick={async () => {
                          await MarkMessageAsRead(msg.messageid, userid);

                          if (Messages.length > 1) {
                            setMessages((prev) =>
                              prev.filter((m) => m.messageid !== msg.messageid),
                            );
                          } else {
                            ProcessClose();
                          }
                        }}
                      />

                      <Button2
                        name="remind"
                        text="Remind Me Again"
                        type="button"
                        classname="submit-btn modal-submit"
                        onClick={() => {
                          if (Messages.length > 1) {
                            setMessages((prev) =>
                              prev.filter((m) => m.messageid !== msg.messageid),
                            );
                          } else {
                            ProcessClose();
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p>No messages found</p>
            )}
          </p>
        )}
      </div>
    </div>
  );
}
